import { post } from "@libs:axios";
import {
  GetSummaryFromFileApiSummarizationFilePostData,
  GetSummaryFromTextApiSummarizationTextPostData,
  GetSummaryFromUrlApiSummarizationUrlPostData,
} from "@openapi:types.gen";
import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { omit } from "lodash";
import { CamelCasedPropertiesDeep } from "type-fest/source/camel-cased-properties-deep";

export interface Summarization {
  FILE: {
    RequestBody: CamelCasedPropertiesDeep<
      GetSummaryFromFileApiSummarizationFilePostData["body"]
    >;
    RequestQuery: CamelCasedPropertiesDeep<
      GetSummaryFromFileApiSummarizationFilePostData["query"]
    >;
    Response: string;
  };
  TEXT: {
    RequestBody: CamelCasedPropertiesDeep<
      GetSummaryFromTextApiSummarizationTextPostData["body"]
    >;
    RequestQuery: CamelCasedPropertiesDeep<
      GetSummaryFromTextApiSummarizationTextPostData["query"]
    >;
    Response: string;
  };
  URL: {
    RequestQuery: CamelCasedPropertiesDeep<
      GetSummaryFromUrlApiSummarizationUrlPostData["query"]
    >;
    Response: string;
  };
}

export const useMutationSummarizationFile = (
  options?: Omit<
    UseMutationOptions<
      Summarization["FILE"]["Response"],
      AxiosError,
      Summarization["FILE"]["RequestBody"] &
        Summarization["FILE"]["RequestQuery"]
    >,
    "mutationKey"
  >,
) => {
  return useMutation<
    Summarization["FILE"]["Response"],
    AxiosError,
    Summarization["FILE"]["RequestBody"] & Summarization["FILE"]["RequestQuery"]
  >({
    mutationKey: ["summarization", "file"],
    mutationFn: (data) => {
      return post<Summarization["FILE"]["Response"]>(
        "/api/summarization/file",
        omit(data, "botId"),
        {
          params: {
            botId: data.botId,
          },
        },
      );
    },
    ...options,
  });
};

export const useMutationSummarizationText = (
  options?: Omit<
    UseMutationOptions<
      Summarization["TEXT"]["Response"],
      AxiosError,
      Summarization["TEXT"]["RequestBody"] &
        Summarization["TEXT"]["RequestQuery"]
    >,
    "mutationKey"
  >,
) => {
  return useMutation<
    Summarization["TEXT"]["Response"],
    AxiosError,
    Summarization["TEXT"]["RequestBody"] & Summarization["TEXT"]["RequestQuery"]
  >({
    mutationKey: ["summarization", "text"],
    mutationFn: (data) => {
      return post<Summarization["TEXT"]["Response"]>(
        "/api/summarization/text",
        omit(data, "botId"),
        {
          params: {
            botId: data.botId,
          },
        },
      );
    },
    ...options,
  });
};

export const useMutationSummarizationUrl = (
  options?: Omit<
    UseMutationOptions<
      Summarization["URL"]["Response"],
      AxiosError,
      Summarization["URL"]["RequestQuery"] &
        Summarization["URL"]["RequestQuery"]
    >,
    "mutationKey"
  >,
) => {
  return useMutation<
    Summarization["URL"]["Response"],
    AxiosError,
    Summarization["URL"]["RequestQuery"] & Summarization["URL"]["RequestQuery"]
  >({
    mutationKey: ["summarization", "url"],
    mutationFn: (data) => {
      return post<Summarization["URL"]["Response"]>(
        "/api/summarization/url",
        undefined,
        {
          params: {
            botId: data.botId,
            url: data.url,
          },
        },
      );
    },
    ...options,
  });
};

export type SummarizationUnifiedData =
  | { type: "file"; file: File; botId: string }
  | { type: "text"; text: string; botId: string }
  | { type: "url"; url: string; botId: string };

export const useMutationSummarization = (
  options?: Omit<
    UseMutationOptions<string, AxiosError, SummarizationUnifiedData>,
    "mutationKey"
  >,
) => {
  return useMutation<string, AxiosError, SummarizationUnifiedData>({
    mutationKey: ["summarization"],
    mutationFn: (data) => {
      switch (data.type) {
        case "file": {
          const formData = new FormData();
          formData.append("file", data.file);
          return post<string>("/api/summarization/file", formData, {
            params: {
              botId: data.botId,
            },
          });
        }
        case "text":
          return post<string>("/api/summarization/text", data.text, {
            params: {
              botId: data.botId,
            },
          });
        case "url":
          return post<string>("/api/summarization/url", undefined, {
            params: {
              botId: data.botId,
              url: data.url,
            },
          });
      }
    },
    ...options,
  });
};
